import {
  Ids,
  Image,
  ImageUploadResponse,
  Images,
  ImageFileType,
} from "../types/images";
import { getTokenAsync } from "../../../hooks/API/getAuthToken";
import { makeHeaders } from "../tools";

import { MEDIAS_SERVICE_URL } from "./service.helper";
import { FileUploadType } from "@fixa/file-upload";
import {
  NO_WRITE_SIGNED_URLS,
  SignedUrlResponse,
} from "@coworker/apprestructured/src/task-management/services/uploads.service";
import {
  createAuthorizationHeader,
  toJson,
} from "@coworker/apprestructured/src/task-management/services/fetchHelper";

const BASE_URL = MEDIAS_SERVICE_URL + "/images";
const BASE_URL_UPLOADS = MEDIAS_SERVICE_URL + "/uploads";

export const getImagesByMediaId = async (mediaId: string, groupId?: string) => {
  try {
    const token = await getTokenAsync();
    if (!mediaId || !token || typeof token !== "string") {
      return;
    }
    const groupIdParam = groupId
      ? `?groupId=${encodeURIComponent(groupId)}`
      : "";
    const response = await fetch(
      `${BASE_URL}/medias/${mediaId}${groupIdParam}`,
      {
        method: "GET",
        headers: makeHeaders(token),
      }
    );

    if (response.status === 404) return [];

    if (!response.ok) {
      throw new Error("Network response was not ok.");
    }
    return (await response.json()).images as Images;
  } catch (error) {
    console.error("Fetch error:", error);
  }

  return null;
};

export const getSignedUrl = async (
  fileNames: string[],
  size: "small" | "large"
) => {
  const filenames = fileNames.join(",");
  const token = await getTokenAsync();
  if (!token || typeof token !== "string") {
    return;
  }
  try {
    const response = await fetch(
      `${BASE_URL}/get_signed_urls?filenames=${filenames}&size=${size}`,
      {
        method: "GET",
        headers: makeHeaders(token),
      }
    );

    if (!response.ok) {
      throw new Error("Network response was not ok.");
    }

    const data: ImageFileType = (await response.json()) as ImageFileType;
    return data;
  } catch (error) {
    console.error("Fetch error:", error);
  }
  return null;
};

export async function getWriteSignedUrls(
  fileNames: string[],
  uploadType: FileUploadType
): Promise<SignedUrlResponse[]> {
  if (fileNames.length === 0) {
    return NO_WRITE_SIGNED_URLS;
  }
  const urlSearchParams = new URLSearchParams();
  urlSearchParams.append("fileNames", JSON.stringify(fileNames));
  urlSearchParams.append("uploadType", uploadType);
  return toJson<SignedUrlResponse[]>(
    await fetch(
      `${BASE_URL_UPLOADS}/write-signed-urls?${urlSearchParams.toString()}`,
      {
        method: "GET",
        headers: {
          ...(await createAuthorizationHeader()),
          "Content-Type": "application/json",
        },
      }
    )
  );
}

export const uploadImageToCloud = async (imageFile: File) => {
  try {
    const token = await getTokenAsync();
    const formData = new FormData();

    formData.append("image", imageFile);
    const response = await fetch(`${BASE_URL}/image-upload`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error("Network response was not ok.");
    }

    const image = (await response.json()) as ImageUploadResponse;
    return image;
  } catch (error) {
    console.error("Fetch error:", error);
  }

  return null;
};

export const addImageToMedia = async (image: Image) => {
  const token = await getTokenAsync();
  if (!token || typeof token !== "string") {
    return;
  }
  const response = await fetch(
    `${BASE_URL}/medias/${image.mediaId}/image-upload`,
    {
      method: "POST",
      headers: makeHeaders(token),
      body: JSON.stringify([image]),
    }
  );

  if (!response.ok) {
    throw new Error("Network response was not ok.");
  }

  const ids = (await response.json()) as Ids;
  return ids;
};

export const deleteMediaImages = async (imageIds: string) => {
  const token = await getTokenAsync();
  if (!token || typeof token !== "string") {
    return;
  }
  const urlSearchParams = new URLSearchParams();
  urlSearchParams.append("imageIds", imageIds);
  const response = await fetch(`${BASE_URL}?${urlSearchParams.toString()}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error("Network response was not ok.");
  }

  return response;
};

export const updateImages = async (mediaId: string, images: Image[]) => {
  const token = await getTokenAsync();
  if (!token || typeof token !== "string") {
    return;
  }
  const response = await fetch(`${BASE_URL}/medias/${mediaId}`, {
    method: "PATCH",
    headers: makeHeaders(token),
    body: JSON.stringify(images),
  });

  if (!response.ok) {
    throw new Error("Network response was not ok.");
  }

  return await response.text();
};
